import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import map from 'lodash/map';
import includes from 'lodash/includes';
import cloneDeep from 'lodash/cloneDeep';
import filter from 'lodash/filter';

import themeable from 'zignal-landscape/lib/themeable';
import Grid from 'zignal-landscape/lib/Grid';
import Row from 'zignal-landscape/lib/Row';
import Col from 'zignal-landscape/lib/Col';
import GridContent from 'zignal-landscape/lib/GridContent';

import CategoryGraphSection from './components/CategoryGraphSection';
import {filterNodes} from '../../dataUtils';
import {drawNodeToCanvas} from '../ClusterGraph/drawingUtils';

class CategoryGraph extends React.Component {

  shouldComponentUpdate(nextProps) {
    return (
      this.props.hoverMap.hoverId !== nextProps.hoverMap.hoverId
      || this.props.sizeBy !== nextProps.sizeBy
      || this.props.showSelectionOnly !== nextProps.showSelectionOnly
      || this.props.selectedViewByOption !== nextProps.selectedViewByOption
      || this.props.clusterNodes !== nextProps.clusterNodes
      || this.props.filteredAutomationScore !== nextProps.filteredAutomationScore
      || (
        this.props.showSelectionOnly
        && this.props.selectedTopClustersByVolume.length !== nextProps.selectedTopClustersByVolume.length
      )
    );
  }

  render() {
    const {
      styles,
      filteredAutomationScore,
      authorNodes,
      clusterNodes,
      sizeBy,
      setGraphHover,
      showSelectionOnly,
      selectedTopClustersByVolume,
      hoverMap,
      tooltip
    } = this.props;

    const selectedClusters = showSelectionOnly ? selectedTopClustersByVolume : [];
    const { tooltipNode, categoryId } = hoverMap;

    const allNodes = filterNodes({
      authorNodes,
      clusterNodes,
      filteredAutomationScore,
      selectedClusters
    });

    let clustersToDisplay = clusterNodes;
    if (showSelectionOnly) {
      clustersToDisplay = filter(clustersToDisplay, ({id}) => includes(selectedTopClustersByVolume, id));
    }

    // eslint-disable-next-line max-params
    const createGraphColumn = (nodes, graphLegend, clusterId, displayIndex, avgAutomationScore) => {
      const card = { [styles.Card]: true };
      const isCategoryHighlighted = categoryId && clusterId === categoryId;
      const hasTooltip = isCategoryHighlighted && !!tooltipNode;

      if (categoryId){
        card[styles.CardOpaque] = !isCategoryHighlighted;
        // if hasTooltip, then author node is hovered on and we should apply class
        // otherwise it is a clusterNode hover and we don't need the class
        card[styles.CardHover] = hasTooltip;
      }

      return (
        <Col xs={3} key={graphLegend}>
          <GridContent className={styles.GridContent}>
            <div className={classnames(card)}>
              <div className={styles.GraphContainer}>
                <CategoryGraphSection
                  nodes={nodes}
                  setGraphHover={setGraphHover}
                  sizeBy={sizeBy}
                  categoryId={clusterId}
                  avgAutomationScore={avgAutomationScore}
                  displayIndex={displayIndex}
                  drawNodeToCanvas={drawNodeToCanvas}
                  hoverMap={isCategoryHighlighted ? hoverMap : {}}
                  tooltip={hasTooltip ? tooltip : null}
                />
              </div>
              <div className={styles.GraphLegend}>{graphLegend}</div>
            </div>
          </GridContent>
        </Col>
      );
    };

    const graphCols = map(clustersToDisplay, ({authorIds, label, id: clusterId, avgAutomationScore}, displayIndex) => {
      const nodesInIds = filter(allNodes, ({id}) => includes(authorIds, id));
      const nodes = cloneDeep(nodesInIds);
      return createGraphColumn(nodes, label, clusterId, displayIndex, avgAutomationScore);
    });

    return (
      <div className={styles.CategoryGraph}>
        <Grid fluid>
          <Row>
            {graphCols.length
              ? graphCols
              : <div className={styles.ContainerEmpty} />
            }
          </Row>
        </Grid>
      </div>
    );
  }
}

CategoryGraph.propTypes = {
  styles: PropTypes.object.isRequired,
  sizeBy: PropTypes.string.isRequired,
  filteredAutomationScore: PropTypes.object,
  authorNodes: PropTypes.array,
  clusterNodes: PropTypes.array,
  setGraphHover: PropTypes.func,
  selectedTopClustersByVolume: PropTypes.array,
  selectedViewByOption: PropTypes.string,
  showSelectionOnly: PropTypes.bool,
  hoverMap: PropTypes.object,
  tooltip: PropTypes.node,
  avgAutomationScore: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ])
};

CategoryGraph.defaultProps = {
  hoverMap: {},
  authorNodes: [],
  clusterNodes: [],
  selectedTopClustersByVolume: [],
  filteredAutomationScore: {}
};

export default themeable(CategoryGraph);
